// @ts-check
/** @typedef {import('../types/api').Product} Product */
/** @typedef {import('../types/api').PaginatedProducts} PaginatedProducts */
/** @typedef {import('../types/api').ProductTaxonomy} ProductTaxonomy */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const adminProductService = {
  /**
   * @param {Object} [params]
   * @returns {Promise<PaginatedProducts>}
   */
  async getAllProducts(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.collection_id) queryParams.append('collection_id', params.collection_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.missing_shipping_data) queryParams.append('missing_shipping_data', 'true');

    const data = await api.get(`/api/admin/products/?${queryParams}`);
    assertShape(data, ['items', 'total'], 'adminProductService.getAllProducts');
    return data;
  },

  /**
   * @param {string} productId
   * @returns {Promise<Product>}
   */
  async getProduct(productId) {
    const data = await api.get(`/api/admin/products/${productId}`);
    assertShape(data, ['id'], 'adminProductService.getProduct');
    return data;
  },

  /**
   * @param {Object} productData
   * @returns {Promise<Product>}
   */
  async createProduct(productData) {
    return api.post('/api/admin/products/', productData);
  },

  /**
   * @param {string} productId
   * @param {Object} productData
   * @returns {Promise<Product>}
   */
  async updateProduct(productId, productData) {
    return api.put(`/api/admin/products/${productId}`, productData);
  },

  /**
   * @param {string} productId
   * @param {boolean} isPublished
   * @returns {Promise<Product>}
   */
  async togglePublish(productId, isPublished) {
    return api.patch(`/api/admin/products/${productId}/publish?is_published=${isPublished}`);
  },

  /**
   * @param {string} productId
   * @returns {Promise<void>}
   */
  async deleteProduct(productId) {
    return api.del(`/api/admin/products/${productId}`);
  },

  /**
   * @param {string} productId
   * @returns {Promise<ProductTaxonomy>}
   */
  async getProductTaxonomy(productId) {
    const data = await api.get(`/api/admin/products/${productId}/taxonomy`);
    assertShape(data, ['event_ids', 'mood_ids', 'place_ids', 'feature_ids'], 'adminProductService.getProductTaxonomy');
    return data;
  },

  /**
   * @param {string} productId
   * @param {Object} data
   * @returns {Promise<ProductTaxonomy>}
   */
  async updateProductTaxonomy(productId, data) {
    return api.put(`/api/admin/products/${productId}/taxonomy`, data);
  },

  /**
   * @param {string} productId
   * @param {boolean} isFeatured
   * @returns {Promise<Product>}
   */
  async toggleFeature(productId, isFeatured) {
    return api.patch(`/api/admin/products/${productId}/feature?is_featured=${isFeatured}`);
  },

  /**
   * @returns {Promise<Product[]>}
   */
  async getFeaturedProducts() {
    const data = await api.get('/api/admin/products/featured/list');
    assertArray(data, 'adminProductService.getFeaturedProducts');
    return data;
  },

  /**
   * @param {Object[]} items
   * @returns {Promise<Object>}
   */
  async reorderFeaturedProducts(items) {
    return api.patch('/api/admin/products/reorder-featured', items);
  },
};

export default adminProductService;
